import SaveStoryAndVideoService, {
  createVideoCreatorSaveAdapter,
} from '@src/services/SaveStoryAndVideoService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import RepublishOnSaveModal from './RepublishOnSaveModal';
import RetrySavingModal from './RetrySavingModal';

export const SaveButton: React.FC = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const isAnySaving = videoCreator.isSaving || videoCreator.isBackgroundSaving;
  const isEnabled = videoCreator.isVideoCreatorReady;
  const savingError = videoCreator.savingError;
  // Adapter to satisfy narrowed VideoCreatorStoreLike dependency and avoid leaking whole store surface.
  const saveService = useMemo(
    () =>
      new SaveStoryAndVideoService({
        videoCreator: createVideoCreatorSaveAdapter(videoCreator),
      }),
    [videoCreator],
  );

  const handleSaveData = async (asFinal: boolean) => {
    if (isAnySaving) return;
    const result = await saveService.save(asFinal);
    if (!result.ok) {
      // Could surface toast / error handling here later
      return;
    }
  };

  return (
    <>
      <Main
        data-testid="creator-studio-save-button"
        disabled={!isEnabled}
        active={isAnySaving}
        onClick={async () => {
          await handleSaveData(false);
        }}
      >
        {isAnySaving ? 'Saving' : 'Save'}
      </Main>
      {videoCreator.showRepublishOnSaveModal && (
        <RepublishOnSaveModal
          closeModal={() => videoCreator.setShowRepublishOnSaveModal(false)}
        />
      )}
      {savingError && (
        <RetrySavingModal
          closeModal={() => (videoCreator.savingError = undefined)}
        />
      )}
    </>
  );
});

export const Main = styled.div.attrs(
  (props: { active: boolean; disabled: boolean }) => props,
)`
  box-sizing: border-box;
  height: 32px;
  min-width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--border);
  color: var(--text-secondary);
  background-color: var(--surface);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  padding: 8px;
  transition: var(--transition-fast);
  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
  &:hover {
    border-color: var(--primary);
  }
  ${props =>
    props.active &&
    css`
      border-color: var(--primary);
    `}
`;
